import { useMemo } from 'react'
import { FileUp } from 'lucide-react'

import { ActivityDashboard } from '@/components/dashboard/activity-dashboard'
import { FitDataView } from '@/components/data/fit-data-view'
import { renderEditableFieldCell } from '@/components/data/render-editable-field-cell'
import { ActivityMap } from '@/components/map/activity-map'
import type { ActivityMapLayout } from '@/components/map/activity-map'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useCurrentActivity } from '@/hooks/use-current-activity'
import { hasGpsData } from '@/lib/geo'
import { cn } from '@/lib/utils'
import { selectSelectedActivity, useActivityStore } from '@/store/activity-store'
import type { LoadedActivity, WorkspaceView } from '@/store/activity-store'

/**
 * Main workspace. No activity loaded -> an instructive empty state.
 * Activity selected -> a Dashboard / FIT data view switch (persisted in
 * UI prefs): the dashboard plus the route map (gracefully degrading to a
 * "no GPS data" message when the activity has none), or the FIT message
 * inspector (`components/data/fit-data-view.tsx`). Dropping FIT files is
 * handled for the whole window by the app shell (`useWindowFileDrop`).
 *
 * Always a flex column, so the dashboard can let the map fill the
 * remaining height (data collapsed / map maximised -- see
 * `DashboardPane`); in the default layout the content is taller than the
 * area and it scrolls as before.
 */
export function Workspace() {
  const activities = useActivityStore((s) => s.activities)
  const selected = useActivityStore(selectSelectedActivity)
  const workspaceView = useActivityStore((s) => s.ui.workspaceView ?? 'dashboard')
  const setWorkspaceView = useActivityStore((s) => s.setWorkspaceView)

  return (
    <main className="relative flex min-w-0 flex-1 flex-col overflow-y-auto p-3 sm:p-4">
      {selected == null ? (
        <EmptyWorkspaceState hasActivities={activities.length > 0} />
      ) : (
        <Tabs
          className="flex flex-1 flex-col"
          value={workspaceView}
          onValueChange={(v) => setWorkspaceView(v as WorkspaceView)}
        >
          <TabsList aria-label="Workspace view">
            <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
            <TabsTrigger value="fitData">FIT data</TabsTrigger>
          </TabsList>
          <TabsContent value="dashboard" className="flex flex-1 flex-col">
            {/* Keyed per activity: within one activity the previous replay stays on screen while an edit replays, so the map isn't torn down on every edit. */}
            <DashboardPane key={selected.id} loaded={selected} />
          </TabsContent>
          <TabsContent value="fitData">
            {/* Keyed per activity so the "show original" toggle / sub-tab reset on selection change. */}
            <FitDataView
              key={selected.id}
              loaded={selected}
              renderFieldCell={renderEditableFieldCell}
            />
          </TabsContent>
        </Tabs>
      )}
    </main>
  )
}

function DashboardPane({ loaded }: { loaded: LoadedActivity }) {
  // Worker-routed and cached per history (see `useCurrentActivity`), so the
  // dashboard's own fetch of the same history is the same round trip.
  const { result } = useCurrentActivity(loaded.history, { keepPreviousWhileLoading: true })
  const activity = result?.activity
  const maximizedPref = useActivityStore((s) => s.ui.mapMaximized === true)
  const collapsed = useActivityStore((s) => s.ui.dashboardCollapsed === true)
  const hasGps = useMemo(() => activity != null && hasGpsData(activity.records), [activity])
  // A persisted "maximised" never hides the data of an activity without a
  // map (no GPS): there is no map to maximise, nor a button to restore.
  const maximized = maximizedPref && hasGps
  const layout: ActivityMapLayout = maximized ? 'fill' : collapsed ? 'grow' : 'fixed'

  if (activity == null) {
    return (
      <p className="text-muted-foreground flex h-full min-h-64 items-center justify-center text-sm">
        Loading &quot;{loaded.fileName}&quot;…
      </p>
    )
  }
  // Same element tree in every layout (the dashboard is hidden, not
  // unmounted, when the map is maximised), so the one `ActivityMap` --
  // and its MapLibre instance and camera -- survives every toggle.
  return (
    <div className={cn('flex flex-col gap-4', layout !== 'fixed' && 'min-h-0 flex-1')}>
      <div hidden={maximized}>
        <ActivityDashboard loaded={loaded} />
      </div>
      <ActivityMap activityId={loaded.id} activity={activity} layout={layout} />
    </div>
  )
}

function EmptyWorkspaceState({ hasActivities }: { hasActivities: boolean }) {
  return (
    <div
      className={cn(
        'border-border text-muted-foreground flex h-full min-h-64 flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed text-center',
      )}
    >
      <FileUp className="h-8 w-8" aria-hidden="true" />
      {hasActivities ? (
        <p className="text-sm">Select an activity from the activity list to view it here.</p>
      ) : (
        <>
          <p className="text-foreground text-sm font-medium">No FIT file loaded yet.</p>
          <p className="max-w-xs text-sm">
            Drag and drop one or more FIT files here, or use &quot;Open file(s)&quot; in the
            toolbar.
          </p>
        </>
      )}
    </div>
  )
}
